// @flow
import _ from 'lodash';
import update from 'immutability-helper';

import { ADMIN } from 'services/actions/adminActions';
import type { Action } from 'services/actions/Action.type';

export type AdminState = {|
  +roles: Array,
  +campaigns: Array,
  +products: Array,
  +categories: Array,
  +categoriesCampaign: Array,
  +fetchingUserRoles: boolean,
  +fetchingCampaigns: boolean,
  +fetchingProducts: boolean,
  +fetchingUpdatedProducts: boolean,
  +fetchingAllCategories: boolean,
  +fetchingCategoriesCampaign: boolean,
  +updatingCategoriesCampaign: boolean,
|};

export const adminInitialState = {
  roles: [],
  campaigns: [],
  products: [],
  categories: [],
  variants: {},
  categoriesCampaign: [],
  fetchingUserRoles: false,
  fetchingCampaigns: false,
  fetchingProducts: false,
  fetchingUpdatedProducts: {},
  fetchingAllCategories: false,
  fetchingCategoriesCampaign: false,
  updatingCategoriesCampaign: false,
};

export const adminReducer = (
  state: AdminState = adminInitialState,
  action: Action
): AdminState => {
  switch (action.type) {
    case ADMIN.REQUEST_USER_ROLES: {
      return {
        ...state,
        fetchingUserRoles: true,
      };
    }

    case ADMIN.RECEIVE_USER_ROLES: {
      return {
        ...state,
        fetchingUserRoles: false,
        roles: action.roles,
      };
    }

    case ADMIN.RECEIVE_USER_ROLES_ERROR: {
      return {
        ...state,
        fetchingUserRoles: false,
        error: action.error,
      };
    }

    case ADMIN.REQUEST_CAMPAIGNS: {
      return {
        ...state,
        fetchingCampaigns: true,
      };
    }

    case ADMIN.RECEIVE_CAMPAIGNS: {
      return {
        ...state,
        fetchingCampaigns: false,
        campaigns: action.campaigns,
      };
    }

    case ADMIN.RECEIVE_CAMPAIGNS_ERROR: {
      return {
        ...state,
        fetchingCampaigns: false,
        error: action.error,
      };
    }

    case ADMIN.REQUEST_PRODUCTS: {
      return {
        ...state,
        fetchingProducts: true,
      };
    }

    case ADMIN.RECEIVE_PRODUCTS: {
      return {
        ...state,
        fetchingProducts: false,
        products: action.products,
        variants: action.variants,
      };
    }

    case ADMIN.RECEIVE_PRODUCTS_ERROR: {
      return {
        ...state,
        fetchingProducts: false,
        error: action.error,
      };
    }

    case ADMIN.REQUEST_UPDATE_PRODUCTS: {
      return {
        ...state,
        fetchingUpdatedProducts: {
          ...state.fetchingUpdatedProducts,
          [action.rowId]: true,
        },
      };
    }

    case ADMIN.RECEIVE_UPDATED_PRODUCTS: {
      //only replace products that have been updated
      const newProducts = [...state.products].map((product) => {
        const match = _.filter(
          action.updatedProducts,
          (p) => p.legacyPostId === product.legacyPostId
        );
        return match.length ? match[0] : product;
      });
      return {
        ...state,
        fetchingUpdatedProducts: {
          ...state.fetchingUpdatedProducts,
          [action.rowId]: false,
        },
        products: newProducts,
        variants: {
          ...state.variants,
          ...action.variants,
        },
      };
    }

    case ADMIN.RECEIVE_UPDATED_PRODUCTS_ERROR: {
      return {
        ...state,
        fetchingUpdatedProducts: {
          ...state.fetchingUpdatedProducts,
          [action.rowId]: false,
        },
        error: action.error,
      };
    }

    case ADMIN.REQUEST_ALL_CATEGORIES: {
      return {
        ...state,
        fetchingAllCategories: true,
      };
    }

    case ADMIN.RECEIVE_ALL_CATEGORIES: {
      return {
        ...state,
        fetchingAllCategories: false,
        categories: action.categories,
      };
    }

    case ADMIN.RECEIVE_ALL_CATEGORIES_ERROR: {
      return {
        ...state,
        fetchingAllCategories: false,
        error: action.error,
      };
    }

    case ADMIN.REQUEST_CATEGORIES_CAMPAIGN: {
      return {
        ...state,
        fetchingCategoriesCampaign: true,
      };
    }

    case ADMIN.RECEIVE_CATEGORIES_CAMPAIGN: {
      return {
        ...state,
        fetchingCategoriesCampaign: false,
        categoriesCampaign: action.categories,
      };
    }

    case ADMIN.RECEIVE_CATEGORIES_CAMPAIGN_ERROR: {
      return {
        ...state,
        fetchingCategoriesCampaign: false,
        error: action.error,
      };
    }

    case ADMIN.REQUEST_UPDATE_CATEGORIES: {
      return {
        ...state,
        updatingCategoriesCampaign: true,
      };
    }

    case ADMIN.RECEIVE_UPDATED_CATEGORIES: {
      const { parentId, updatedCategories } = action;
      // updated main category
      if (!parentId) {
        return {
          ...state,
          updatingCategoriesCampaign: false,
          categoriesCampaign: updatedCategories,
        };
      }
      // updated child category
      const categoryIndex = state.categoriesCampaign.findIndex(
        (category) => category.id === parentId
      );
      return update(state, {
        updatingCategoriesCampaign: { $set: false },
        categoriesCampaign: {
          [categoryIndex]: {
            children: { $set: updatedCategories },
          },
        },
      });
    }

    case ADMIN.RECEIVE_UPDATED_CATEGORIES_ERROR: {
      return {
        ...state,
        updatingCategoriesCampaign: false,
        error: action.error,
      };
    }

    default: {
      return state;
    }
  }
};

export default adminReducer;
